
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from "@/hooks/use-toast";
import { Mail, Facebook, Twitter, Linkedin, Copy, Instagram, Youtube } from 'lucide-react';

interface ReferralFormProps {
  referralLink: string;
  referralTemplate: string;
  onSendReferral: (data: any, method: string) => Promise<any>;
  campaignId?: string; // Add campaignId prop
}

const ReferralForm: React.FC<ReferralFormProps> = ({ 
  referralLink, 
  referralTemplate, 
  onSendReferral,
  campaignId
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const { toast } = useToast();
  const [isSending, setIsSending] = useState(false);

  // Initialize message template
  useEffect(() => {
    // Initialize with the provided template
    setMessage(referralTemplate.replace('{referralLink}', referralLink));
  }, [referralLink, referralTemplate]);

  const handleSend = async (method: string) => {
    setIsSending(true);
    try {
      // Use the current message
      const messageToSend = message;
      
      const data = { name, email, message: messageToSend, referralLink };
      await onSendReferral(data, method);
      
      toast({
        title: "Referral sent!",
        description: `Referral sent successfully via ${method}.`,
      });
      
      if (method === 'social' && window.localStorage) {
        const userId = JSON.parse(localStorage.getItem('authUser') || '{}')?.id;
        if (userId) {
          localStorage.setItem(`social-referral-sent-${userId}`, 'true');
          
          // Also update the tasks in localStorage
          const tasksKey = `tasks-${userId}`;
          const tasks = JSON.parse(localStorage.getItem(tasksKey) || '[]');
          const updatedTasks = tasks.map((task: any) => 
            task.id === 'social-referral' ? { ...task, completed: true } : task
          );
          localStorage.setItem(tasksKey, JSON.stringify(updatedTasks));
        }
      }
      
      setName('');
      setEmail('');
      
      // Reset message to template
      setMessage(referralTemplate.replace('{referralLink}', referralLink));
      
    } catch (error) {
      console.error("Error sending referral:", error);
      toast({
        variant: "destructive",
        title: "Failed to send referral",
        description: "Please try again.",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(referralLink)
      .then(() => {
        toast({
          title: "Link copied!",
          description: "Referral link copied to clipboard.",
        });
      })
      .catch(err => {
        console.error("Could not copy text: ", err);
        toast({
          variant: "destructive",
          title: "Copy failed",
          description: "Could not copy the referral link to clipboard.",
        });
      });
  };

  const handleShareOnFacebook = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`;
    window.open(facebookUrl, '_blank');
    handleSend('social');
  };

  const handleShareOnTwitter = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(message)}`;
    window.open(twitterUrl, '_blank');
    handleSend('social');
  };

  const handleShareOnLinkedIn = () => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`;
    window.open(linkedInUrl, '_blank');
    handleSend('social');
  };

  const handleShareOnInstagram = () => {
    navigator.clipboard.writeText(referralLink);
    toast({
      title: "Link copied for Instagram",
      description: "Share this link on your Instagram profile or stories",
    });
    handleSend('social');
  };

  const handleShareOnYoutube = () => {
    navigator.clipboard.writeText(referralLink);
    toast({
      title: "Link copied for YouTube",
      description: "Share this link in your YouTube video description or community post",
    });
    handleSend('social');
  };

  const handleShareOnTikTok = () => {
    navigator.clipboard.writeText(referralLink);
    toast({
      title: "Link copied for TikTok",
      description: "Share this link in your TikTok bio or video description",
    });
    handleSend('social');
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter recipient's name"
        />
      </div>
      <div>
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter recipient's email"
        />
      </div>
      <div>
        <Label htmlFor="message">Message</Label>
        <Textarea
          id="message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Customize your message"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Button onClick={() => handleSend('email')} disabled={isSending}>
          {isSending ? "Sending..." : <><Mail className="h-4 w-4 mr-2" /> Send via Email</>}
        </Button>
        <Button variant="secondary" onClick={handleCopyToClipboard}>
          <Copy className="h-4 w-4 mr-2" />
          Copy Referral Link
        </Button>
      </div>
      <div>
        <p className="text-sm text-gray-500">Or share on social media:</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          <Button variant="outline" onClick={handleShareOnFacebook} disabled={isSending}>
            <Facebook className="h-4 w-4 mr-2" /> Facebook
          </Button>
          <Button variant="outline" onClick={handleShareOnTwitter} disabled={isSending}>
            <Twitter className="h-4 w-4 mr-2" /> Twitter
          </Button>
          <Button variant="outline" onClick={handleShareOnLinkedIn} disabled={isSending}>
            <Linkedin className="h-4 w-4 mr-2" /> LinkedIn
          </Button>
          <Button variant="outline" onClick={handleShareOnInstagram} disabled={isSending}>
            <Instagram className="h-4 w-4 mr-2" /> Instagram
          </Button>
          <Button variant="outline" onClick={handleShareOnYoutube} disabled={isSending}>
            <Youtube className="h-4 w-4 mr-2" /> YouTube
          </Button>
          <Button variant="outline" onClick={handleShareOnTikTok} disabled={isSending}>
            <span className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 mr-2">
                <path d="M9 12a4 4 0 1 0 0 8 4 4 0 0 0 0-8z"></path>
                <path d="M15 8a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"></path>
                <path d="M15 8v8a4 4 0 0 1-4 4"></path>
                <line x1="15" y1="4" x2="15" y2="12"></line>
              </svg>
              TikTok
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReferralForm;
